import React, { type FunctionComponent, type PropsWithChildren, useRef } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { autoBeforeEachTest } from '@atlassian/jira-platform-auto-before-each-test/src/auto-before-each-test.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { NULL_STR, OWNER_PREFIX } from './extractors.tsx';

export type OwnershipParams = {
	markerType: 'placeholder' | 'error-boundary';
	/**
	 * Uniquely identifies the code location along with {@link markerType}. The meaning of 'name' also depends on {@link markerType}.
	 */
	name: string;
	packageName?: string;
	teamName?: string;
};

const MARKER_COMPONENTS = new Map<string, FunctionComponent<PropsWithChildren>>();

autoBeforeEachTest(() => {
	MARKER_COMPONENTS.clear();
});

const createKeyForComponent = ({ markerType, name, packageName, teamName }: OwnershipParams) => {
	return `${markerType}/${name}/${packageName ?? NULL_STR}/${teamName ?? NULL_STR}`;
};

export const useOwnershipMarkerComponent = (
	nextParams: OwnershipParams,
): FunctionComponent<PropsWithChildren> => {
	const params = useRef(nextParams).current;
	const componentKey = createKeyForComponent(params);
	const ExistingComponent = MARKER_COMPONENTS.get(componentKey);
	if (ExistingComponent) {
		return ExistingComponent;
	}

	const NewComponent = ({ children }: PropsWithChildren) => children;
	Object.defineProperty(NewComponent, 'name', { value: encodeDisplayName(params) });
	MARKER_COMPONENTS.set(componentKey, NewComponent);
	return NewComponent;
};

/**
 * Creates a stable marker component, used to add debugging information to the React component tree.
 * Multiple calls to this method with the same parameters will produce the exact same component instance.
 *
 * IMPORTANT LIMITATION: this component must remain stable, however the `name` attached to each component instance
 * cannot be modified because it ends up being cached by React. This means this component is only suitable for capturing
 * errors on initial render (e.g. during React hydration), but not after navigating to a different route - this will
 * change the props (unless unmounted) but the owner mark will remain the same.
 */
const OwnershipMarkerImpl = ({ children, ...params }: PropsWithChildren<OwnershipParams>) => {
	const Marker = useOwnershipMarkerComponent(params);
	return <Marker>{children}</Marker>;
};

const StableNoOpComponent = ({ children }: PropsWithChildren<OwnershipParams>) => children;

export const OwnershipMarker = componentWithCondition(
	() => fg('jfp-magma-react-ownership-marker-component'),
	OwnershipMarkerImpl,
	StableNoOpComponent,
);

const encodeDisplayName = ({
	markerType,
	name,
	packageName = NULL_STR,
	teamName = NULL_STR,
}: OwnershipParams) => {
	return `${OWNER_PREFIX}${markerType}___${name}___${packageName}___${teamName}`;
};
